import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/ApiService';
import companionService, {
    CompanionHealth,
    DesktopAgentCapability,
} from '../services/CompanionService';
import DesktopAgentPairingPanel from '../components/DesktopAgentPairingPanel';
import desktopAgentIcon from '@easytools-assets/desktop-agent/desktop-agent.svg';

interface DesktopAgentInfo {
    productCode?: string;
    displayName?: string;
    version: string;
    healthPort: number;
    fileName: string;
    available: boolean;
    sizeBytes?: number | null;
    publishedAtUtc?: string | null;
    runtimeMode?: string;
    requiresAdministrator?: boolean;
    downloadUrl?: string;
}

const FALLBACK_CAPABILITIES: DesktopAgentCapability[] = [
    {
        code: 'diagnostic-analysis',
        displayName: 'Analisi diagnostica locale',
        description: 'Analisi di issue e anomalie tramite le CLI installate sul PC.',
        available: false,
        requiresApproval: false,
    },
    {
        code: 'diagnostic-patch-draft',
        displayName: 'Patch draft read-only',
        description: 'Generazione di diff controllati senza modificare il workspace.',
        available: false,
        requiresApproval: true,
    },
    {
        code: 'ai-crm-analyst',
        displayName: 'AI CRM Analyst locale',
        description: 'Esecuzione dei provider CLI locali del tool AI CRM Analyst.',
        available: false,
        requiresApproval: false,
    },
    {
        code: 'dataverse-mcp-bridge',
        displayName: 'Bridge MCP Dataverse',
        description: 'Accesso controllato ai tool Dataverse autorizzati dal backend.',
        available: false,
        requiresApproval: true,
    },
];

const formatBytes = (value?: number | null): string => {
    if (!value) return '—';
    const megabytes = value / 1024 / 1024;
    return `${megabytes.toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`;
};

const formatDate = (value?: string | null): string => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('it-IT', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
};

const getErrorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : 'Operazione non riuscita.';

const DesktopAgentPage: React.FC = () => {
    const [info, setInfo] = useState<DesktopAgentInfo | null>(null);
    const [health, setHealth] = useState<CompanionHealth | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [packageError, setPackageError] = useState('');
    const [localError, setLocalError] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        setPackageError('');
        setLocalError('');

        let healthPort = info?.healthPort ?? 4977;
        try {
            const packageInfo = await apiService.getCompanionInfo<DesktopAgentInfo>();
            setInfo(packageInfo);
            healthPort = packageInfo.healthPort;
        } catch (error) {
            setPackageError(getErrorMessage(error));
        }

        try {
            setHealth(await companionService.health(healthPort));
        } catch (error) {
            setHealth(null);
            setLocalError(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }, [info?.healthPort]);

    useEffect(() => {
        void refresh();
        // La prima verifica avviene al mount; i refresh successivi sono espliciti.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const capabilities = health?.capabilities?.length
        ? health.capabilities
        : FALLBACK_CAPABILITIES;
    const availableAgents = health?.agents.filter(agent => agent.available) ?? [];
    const versionMatches = Boolean(info && health && info.version === health.version);
    const productName = info?.displayName || health?.displayName || 'IA Desktop Agent';

    const overallStatus = useMemo(() => {
        if (loading) return { label: 'Verifica in corso', css: 'text-bg-secondary', icon: 'bi-arrow-repeat' };
        if (!health) return { label: 'Non rilevato', css: 'text-bg-warning', icon: 'bi-exclamation-triangle' };
        if (!versionMatches) return { label: 'Aggiornamento richiesto', css: 'text-bg-warning', icon: 'bi-arrow-up-circle' };
        return { label: 'Pronto', css: 'text-bg-success', icon: 'bi-check-circle' };
    }, [health, loading, versionMatches]);

    const download = async () => {
        setDownloading(true);
        setPackageError('');
        try {
            const blob = await apiService.downloadDesktopAgent();
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = info?.fileName || 'IADesktopAgent-win-x64.zip';
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            setPackageError(getErrorMessage(error));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="desktop-agent-page">
            <section className="desktop-agent-hero mb-4">
                <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
                    <div className="d-flex align-items-start gap-3">
                        <div className="desktop-agent-hero__icon">
                            <img src={desktopAgentIcon} alt="" aria-hidden="true" />
                        </div>
                        <div>
                            <div className="text-uppercase small fw-semibold text-primary mb-1">Infrastruttura locale Semplora</div>
                            <h2 className="mb-2">{productName}</h2>
                            <p className="mb-0 text-body-secondary">
                                Collega in modo controllato Quality Center e AI CRM Analyst alle CLI,
                                ai workspace e alle risorse disponibili sul PC dell&apos;utente.
                            </p>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className={`badge rounded-pill ${overallStatus.css} px-3 py-2`}>
                            <i className={`bi ${overallStatus.icon} me-2`} />{overallStatus.label}
                        </span>
                        <button className="btn btn-outline-primary" onClick={() => void refresh()} disabled={loading}>
                            <i className="bi bi-arrow-clockwise me-2" />Verifica
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => void download()}
                            disabled={downloading || info?.available === false}>
                            <i className="bi bi-download me-2" />
                            {downloading
                                ? 'Download…'
                                : info?.version ? `Scarica ZIP v${info.version}` : 'Scarica ZIP per Windows'}
                        </button>
                    </div>
                </div>
            </section>

            {packageError && <div className="alert alert-danger"><i className="bi bi-cloud-slash me-2" />{packageError}</div>}
            {localError && (
                <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2" />
                    Agent locale non raggiungibile sulla porta {info?.healthPort ?? 4977}. Scaricalo, estrailo e avvialo come utente standard.
                </div>
            )}

            <div className="alert alert-info border-0 desktop-agent-mvp-note">
                <strong>IA Desktop Agent:</strong> applicazione Windows per-user con system tray, avvio automatico opzionale
                e pairing persistente del dispositivo. I job tipizzati continuano a essere eseguiti tramite il listener locale
                su loopback; pairing e heartbeat consentono all&apos;admin di identificare, monitorare e revocare ogni installazione.
            </div>

            <DesktopAgentPairingPanel
                healthPort={info?.healthPort ?? 4977}
                onAgentChanged={refresh}
            />


            <div className="row g-3 mb-4">
                <div className="col-lg-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header bg-body d-flex align-items-center justify-content-between">
                            <strong>Pacchetto pubblicato</strong>
                            <i className="bi bi-box-seam text-primary" />
                        </div>
                        <div className="card-body">
                            <dl className="row small mb-0 desktop-agent-details">
                                <dt className="col-5">Versione richiesta</dt><dd className="col-7">{info?.version ?? '—'}</dd>
                                <dt className="col-5">Piattaforma</dt><dd className="col-7">Windows x64</dd>
                                <dt className="col-5">File</dt><dd className="col-7"><code>{info?.fileName ?? 'IADesktopAgent-win-x64.zip'}</code></dd>
                                <dt className="col-5">Dimensione</dt><dd className="col-7">{formatBytes(info?.sizeBytes)}</dd>
                                <dt className="col-5">Permessi admin</dt><dd className="col-7">Non richiesti</dd>
                                <dt className="col-5">Pubblicato</dt><dd className="col-7">{formatDate(info?.publishedAtUtc)}</dd>
                                <dt className="col-5">Disponibilità</dt>
                                <dd className="col-7">
                                    <span className={`badge ${info?.available ? 'text-bg-success' : 'text-bg-secondary'}`}>
                                        {info?.available ? 'Disponibile' : 'Non pubblicato'}
                                    </span>
                                </dd>
                            </dl>
                            <button
                                className="btn btn-primary w-100 mt-3"
                                onClick={() => void download()}
                                disabled={downloading || info?.available === false}>
                                <i className="bi bi-file-earmark-zip me-2" />
                                {downloading ? 'Preparazione download…' : `Scarica ${info?.fileName ?? 'lo ZIP per Windows'}`}
                            </button>
                            <div className="small text-body-secondary mt-2">Archivio self-contained: non è necessario installare .NET sul PC.</div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header bg-body d-flex align-items-center justify-content-between">
                            <strong>Agent su questo PC</strong>
                            <i className="bi bi-broadcast-pin text-primary" />
                        </div>
                        <div className="card-body">
                            <dl className="row small mb-3 desktop-agent-details">
                                <dt className="col-5">Versione rilevata</dt><dd className="col-7">{health?.version ?? '—'}</dd>
                                <dt className="col-5">Esecuzione</dt><dd className="col-7">Per-user</dd>
                                <dt className="col-5">Endpoint</dt><dd className="col-7"><code>127.0.0.1:{info?.healthPort ?? 4977}</code></dd>
                                <dt className="col-5">Compatibilità</dt>
                                <dd className="col-7">
                                    {health
                                        ? <span className={`badge ${versionMatches ? 'text-bg-success' : 'text-bg-warning'}`}>{versionMatches ? 'Allineata' : 'Da aggiornare'}</span>
                                        : '—'}
                                </dd>
                            </dl>
                            <div className="d-flex flex-wrap gap-2">
                                {health?.agents.map(agent => (
                                    <span key={agent.code} className={`badge ${agent.available ? 'text-bg-primary' : 'text-bg-light border text-body-secondary'}`}>
                                        {agent.displayName}{agent.version ? ` · ${agent.version}` : ''}
                                    </span>
                                ))}
                            </div>
                            {health && availableAgents.length === 0 && (
                                <div className="small text-warning mt-3">Agent attivo, ma nessuna CLI supportata è stata rilevata.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <section className="mb-4">
                <div className="d-flex align-items-end justify-content-between mb-3">
                    <div>
                        <h4 className="mb-1">Capability</h4>
                        <div className="small text-body-secondary">Funzioni condivise disponibili per i tool Semplora.</div>
                    </div>
                </div>
                <div className="row g-3">
                    {capabilities.map(capability => (
                        <div className="col-md-6 col-xl-3" key={capability.code}>
                            <div className="card h-100 desktop-agent-capability">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                                        <i className="bi bi-grid-1x2-fill text-primary fs-4" />
                                        <span className={`badge ${capability.available ? 'text-bg-success' : 'text-bg-secondary'}`}>
                                            {capability.available ? 'Pronta' : 'Non rilevata'}
                                        </span>
                                    </div>
                                    <h6>{capability.displayName}</h6>
                                    <p className="small text-body-secondary mb-2">{capability.description}</p>
                                    {capability.requiresApproval && (
                                        <span className="small text-primary"><i className="bi bi-person-check me-1" />Con approvazione</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <div className="row g-4">
                <div className="col-xl-7">
                    <section className="card shadow-sm h-100">
                        <div className="card-header bg-body"><h5 className="mb-0">Installazione e utilizzo</h5></div>
                        <div className="card-body">
                            <ol className="desktop-agent-steps list-unstyled mb-0">
                                <li><span>1</span><div><strong>Scarica lo ZIP</strong><p>Usa il pulsante in questa pagina per ottenere la versione Windows x64 pubblicata dal server.</p></div></li>
                                <li><span>2</span><div><strong>Estrai in una cartella stabile</strong><p>Ad esempio <code>%LOCALAPPDATA%\EasyTools\DesktopAgent</code>. Non avviare l&apos;eseguibile direttamente dallo ZIP e non spostare la cartella dopo aver abilitato l&apos;autostart.</p></div></li>
                                <li><span>3</span><div><strong>Avvia come utente standard</strong><p>Esegui <code>IADesktopAgent.exe</code>, senza “Esegui come amministratore”. L&apos;icona comparirà nella system tray.</p></div></li>
                                <li><span>4</span><div><strong>Verifica e associa il PC</strong><p>Premi “Verifica”, quindi “Associa questo PC”. Il codice monouso viene completato automaticamente dall&apos;agent locale.</p></div></li>
                                <li><span>5</span><div><strong>Abilita l&apos;avvio al login</strong><p>Attiva l&apos;opzione nel riquadro superiore oppure dal menu della tray. La configurazione vale solo per l&apos;utente Windows corrente.</p></div></li>
                                <li><span>6</span><div><strong>Usa le CLI locali</strong><p>Seleziona la modalità CLI in AI CRM Analyst o nel Quality Center. Stato e heartbeat del dispositivo saranno visibili in questa pagina.</p></div></li>
                            </ol>
                            <div className="alert alert-light border mt-4 mb-0 small">
                                <strong>Aggiornamento:</strong> scegli “Esci” dalla tray, sostituisci i file nella stessa cartella con quelli del nuovo ZIP e riavvia l&apos;agent.
                                Identità e pairing sono conservati nel profilo Windows e non vengono persi sostituendo la cartella applicativa.
                            </div>
                        </div>
                    </section>
                </div>
                <div className="col-xl-5">
                    <section className="card shadow-sm h-100">
                        <div className="card-header bg-body"><h5 className="mb-0">Modello di sicurezza</h5></div>
                        <div className="card-body">
                            <ul className="list-unstyled desktop-agent-security mb-0">
                                <li><i className="bi bi-person-lock" /><span><strong>Permessi dell&apos;utente</strong><small>Nessun account di servizio o privilegio LocalSystem.</small></span></li>
                                <li><i className="bi bi-router" /><span><strong>Solo loopback</strong><small>L&apos;endpoint non è raggiungibile dalla rete aziendale.</small></span></li>
                                <li><i className="bi bi-key" /><span><strong>Pairing monouso</strong><small>Il codice scade dopo cinque minuti e può essere utilizzato una sola volta.</small></span></li>
                                <li><i className="bi bi-shield-lock" /><span><strong>Credenziale protetta</strong><small>Hash sul server e segreto cifrato con Windows DPAPI sul PC.</small></span></li>
                                <li><i className="bi bi-terminal" /><span><strong>Comandi tipizzati</strong><small>Il browser non invia command line arbitrarie.</small></span></li>
                            </ul>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DesktopAgentPage;
